import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sun, 
  Moon, 
  Monitor, 
  User, 
  Bell, 
  Shield, 
  CreditCard, 
  Zap, 
  Settings,
  Edit,
  Check,
  X,
  Mail,
  Phone,
  Building,
  Globe,
  Key,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Trash2,
  Plus,
  Link,
  Database,
  Cloud,
  Smartphone,
  Calendar,
  Headphones,
  BarChart3,
  Target,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/Button/Button';
import { useTheme } from '../theme/ThemeProvider';
import { cn } from '../utils/cn';

// Settings interfaces
interface SettingsTab {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

interface ToggleOption {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
}

interface IntegrationOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
  status: 'active' | 'inactive' | 'error';
}

// Settings card component
const SettingsCard: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 hover:border-neutral-600/50 transition-all duration-300 shadow-xl"
    >
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        <p className="text-neutral-400">{description}</p>
      </div>
      {children}
    </motion.div>
  );
};

// Toggle switch component
const ToggleSwitch: React.FC<{
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}> = ({ enabled, onChange }) => {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
        enabled ? 'bg-blue-500' : 'bg-neutral-600'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
          enabled ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
};

// Input field component
const InputField: React.FC<{
  label: string;
  value: string;
  type?: string;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ label, value, type = 'text', placeholder, onChange }) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-300">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
      />
    </div>
  );
};

// Select field component
const SelectField: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-300">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all appearance-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-neutral-800">
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { mode, setMode } = useTheme();
  const [activeTab, setActiveTab] = useState<string>('profile');
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corp',
    role: 'Sales Manager'
  });

  const [notifications, setNotifications] = useState<ToggleOption[]>([
    { id: 'email', label: 'Email Notifications', description: 'Receive updates via email', enabled: true },
    { id: 'push', label: 'Push Notifications', description: 'Browser push notifications', enabled: false },
    { id: 'marketing', label: 'Marketing Updates', description: 'Product updates and tips', enabled: true },
    { id: 'security', label: 'Security Alerts', description: 'Important security notifications', enabled: true }
  ]);

  const [integrations, setIntegrations] = useState<IntegrationOption[]>([
    { id: 'salesforce', name: 'Salesforce', description: 'Sync contacts and deals', icon: '☁️', connected: true, status: 'active' },
    { id: 'hubspot', name: 'HubSpot', description: 'CRM integration', icon: '🧡', connected: false, status: 'inactive' },
    { id: 'gmail', name: 'Gmail', description: 'Email sync and tracking', icon: '📧', connected: true, status: 'active' },
    { id: 'linkedin', name: 'LinkedIn', description: 'Social selling integration', icon: '💼', connected: true, status: 'error' },
    { id: 'zapier', name: 'Zapier', description: 'Workflow automation', icon: '⚡', connected: false, status: 'inactive' },
    { id: 'slack', name: 'Slack', description: 'Team notifications', icon: '💬', connected: true, status: 'active' }
  ]);

  const tabs: SettingsTab[] = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: 2 },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'integrations', label: 'Integrations', icon: Zap, badge: 3 },
    { id: 'preferences', label: 'Preferences', icon: Settings }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light', description: 'Clean, bright interface', icon: Sun },
    { value: 'dark', label: 'Dark', description: 'Easy on the eyes', icon: Moon },
    { value: 'system', label: 'System', description: 'Follow system preference', icon: Monitor }
  ];

  const handleNotificationToggle = (id: string, enabled: boolean) => {
    setNotifications(prev => 
      prev.map(notif => notif.id === id ? { ...notif, enabled } : notif)
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
  return (
          <div className="space-y-6">
            <SettingsCard
              title="Personal Information"
              description="Update your personal details and contact information"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="First Name"
                  value={profileData.firstName}
                  onChange={(value) => setProfileData(prev => ({ ...prev, firstName: value }))}
                />
                <InputField
                  label="Last Name"
                  value={profileData.lastName}
                  onChange={(value) => setProfileData(prev => ({ ...prev, lastName: value }))}
                />
                <InputField
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(value) => setProfileData(prev => ({ ...prev, email: value }))}
                />
                <InputField
                  label="Phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={(value) => setProfileData(prev => ({ ...prev, phone: value }))}
                />
                <InputField
                  label="Company"
                  value={profileData.company}
                  onChange={(value) => setProfileData(prev => ({ ...prev, company: value }))}
                />
                <InputField
                  label="Role"
                  value={profileData.role}
                  onChange={(value) => setProfileData(prev => ({ ...prev, role: value }))}
                />
              </div>
              <div className="flex justify-end pt-4">
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
        </div>
            </SettingsCard>

            <SettingsCard
              title="Profile Picture"
              description="Upload a profile picture to personalize your account"
            >
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-semibold">
                    {profileData.firstName.charAt(0)}{profileData.lastName.charAt(0)}
                  </span>
                            </div>
                            <div className="flex-1">
                  <p className="text-neutral-300 mb-2">Upload a new profile picture</p>
                  <div className="flex space-x-3">
                    <Button variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                    <Button variant="outline" size="sm">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            </SettingsCard>
          </div>
        );

      case 'notifications':
        return (
          <SettingsCard
            title="Notification Preferences"
            description="Choose how you want to be notified about important updates"
          >
            <div className="space-y-6">
              {notifications.map((notification) => (
                <div key={notification.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">{notification.label}</h4>
                    <p className="text-neutral-400 text-sm">{notification.description}</p>
                  </div>
                  <ToggleSwitch
                    enabled={notification.enabled}
                    onChange={(enabled) => handleNotificationToggle(notification.id, enabled)}
                  />
                </div>
              ))}
            </div>
          </SettingsCard>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <SettingsCard
              title="Password & Authentication"
              description="Manage your password and two-factor authentication"
            >
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Change Password</h4>
                    <p className="text-neutral-400 text-sm">Update your account password</p>
                  </div>
                  <Button variant="outline">
                    <Key className="w-4 h-4 mr-2" />
                    Change
                  </Button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Two-Factor Authentication</h4>
                    <p className="text-neutral-400 text-sm">Add an extra layer of security</p>
                  </div>
                  <Button className="bg-gradient-to-r from-green-500 to-emerald-600">
                    <Shield className="w-4 h-4 mr-2" />
                    Enable
                  </Button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Login Sessions</h4>
                    <p className="text-neutral-400 text-sm">Manage active login sessions</p>
                  </div>
                  <Button variant="outline">
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              title="Privacy Settings"
              description="Control your privacy and data sharing preferences"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Data Analytics</h4>
                    <p className="text-neutral-400 text-sm">Help improve our services</p>
                  </div>
                  <ToggleSwitch enabled={true} onChange={() => {}} />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Marketing Communications</h4>
                    <p className="text-neutral-400 text-sm">Receive product updates</p>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} />
                </div>
              </div>
            </SettingsCard>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <SettingsCard
              title="Current Plan"
              description="Manage your subscription and billing information"
            >
              <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white">Professional Plan</h3>
                    <p className="text-neutral-300">$99/month • Billed monthly</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">$99</div>
                    <div className="text-neutral-400 text-sm">per month</div>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Change Plan
                  </Button>
                  <Button variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              title="Payment Method"
              description="Manage your payment methods and billing details"
            >
              <div className="space-y-4">
                <div className="bg-neutral-800/50 border border-neutral-700 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded"></div>
                      <div>
                        <p className="text-white font-medium">•••• •••• •••• 4242</p>
                        <p className="text-neutral-400 text-sm">Expires 12/25</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </SettingsCard>
          </div>
        );

      case 'integrations':
        return (
          <SettingsCard
            title="Connected Services"
            description="Manage your integrations with third-party services"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {integrations.map((integration) => (
                <div key={integration.id} className="bg-neutral-800/50 border border-neutral-700 rounded-xl p-4 hover:border-neutral-600 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{integration.icon}</div>
                      <div>
                        <h4 className="text-white font-medium">{integration.name}</h4>
                        <p className="text-neutral-400 text-sm">{integration.description}</p>
                      </div>
                    </div>
                    <div className={cn(
                      "w-3 h-3 rounded-full",
                      integration.status === 'active' ? 'bg-green-400' :
                      integration.status === 'error' ? 'bg-red-400' :
                      'bg-neutral-500'
                    )} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={cn(
                      "text-xs px-2 py-1 rounded-full",
                      integration.connected ? 'bg-green-500/20 text-green-400' : 'bg-neutral-500/20 text-neutral-400'
                    )}>
                      {integration.connected ? 'Connected' : 'Not Connected'}
                    </span>
                  <Button variant="outline" size="sm">
                      {integration.connected ? 'Configure' : 'Connect'}
                  </Button>
                  </div>
                </div>
              ))}
            </div>
          </SettingsCard>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <SettingsCard
              title="Appearance"
              description="Customize how the interface looks and feels"
            >
              <div>
                <label className="text-sm font-medium text-neutral-300 block mb-4">Theme</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {themeOptions.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = mode === option.value;
                    
                    return (
                      <motion.button
                        key={option.value}
                        onClick={() => setMode(option.value as 'light' | 'dark' | 'system')}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all text-left",
                          isSelected
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-neutral-700 hover:border-neutral-600'
                        )}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            isSelected ? 'bg-blue-500/30 text-blue-400' : 'bg-neutral-800 text-neutral-400'
                          )}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className={cn(
                              "font-medium",
                              isSelected ? 'text-blue-300' : 'text-white'
                            )}>
                              {option.label}
                            </div>
                            <div className={cn(
                              "text-sm",
                              isSelected ? 'text-blue-400' : 'text-neutral-400'
                            )}>
                              {option.description}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              title="General Preferences"
              description="Configure your general application preferences"
            >
              <div className="space-y-6">
                <SelectField
                  label="Default Language"
                  value="en"
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'es', label: 'Spanish' },
                    { value: 'fr', label: 'French' },
                    { value: 'de', label: 'German' }
                  ]}
                  onChange={() => {}}
                />
                
                <SelectField
                  label="Timezone"
                  value="utc-5"
                  options={[
                    { value: 'utc-5', label: 'Eastern Time (UTC-5)' },
                    { value: 'utc-6', label: 'Central Time (UTC-6)' },
                    { value: 'utc-7', label: 'Mountain Time (UTC-7)' },
                    { value: 'utc-8', label: 'Pacific Time (UTC-8)' }
                  ]}
                  onChange={() => {}}
                />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium">Auto-save drafts</h4>
                      <p className="text-neutral-400 text-sm">Automatically save draft content</p>
                    </div>
                    <ToggleSwitch enabled={true} onChange={() => {}} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium">Smart suggestions</h4>
                      <p className="text-neutral-400 text-sm">Get AI-powered content suggestions</p>
                    </div>
                    <ToggleSwitch enabled={true} onChange={() => {}} />
                  </div>
                </div>
              </div>
            </SettingsCard>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
            Settings
          </h1>
          <p className="text-xl text-neutral-400">
            Configure your account and preferences
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Tabs */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="w-full lg:w-64 flex-shrink-0"
          >
            <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-4 shadow-xl">
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3 text-left rounded-xl transition-all duration-200',
                        isActive
                          ? 'bg-blue-500/90 text-white shadow-lg'
                          : 'text-neutral-400 hover:text-white hover:bg-neutral-800/60'
                      )}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{tab.label}</span>
                      </div>
                      {tab.badge && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Content Area */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-1"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 